import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  User,
  IndianRupee,
  Plus,
  Trash2,
  Syringe,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { billingApi } from "@/api/billing";
import { paymentApi } from "@/api/payment";
import {
  additionalServicesApi,
  AdditionalService,
} from "@/api/additionalServices";
import {
  BillCreateData,
  BillStatus,
  BillType,
  PaymentCreateData,
  PaymentMethod,
} from "@/types/types";
import { useAuth } from "@/contexts/AuthContext";
import PaymentSplitsEditor, {
  getSplitsError,
  getSplitsTotal,
  PaymentSplitDraft,
} from "@/components/billing/PaymentSplitsEditor";
import ViewBill from "@/components/billing/ViewBill";
import { patientApi } from "@/api/patient";
import { doctorApi } from "@/api/doctor";
import { DoctorSearchableSelect } from "@/components/common/DoctorSearchableSelect";
import { patientNeedsRegistrationSourceCapture } from "@/lib/patientRegistrationSource";
import RegistrationSourceCaptureDialog from "@/components/patient/RegistrationSourceCaptureDialog";
import { Checkbox } from "@/components/ui/checkbox";

const DERMATOLOGY_GST_PERCENT = 5;

type LineDraft = {
  key: string;
  serviceId: string;
  unitPrice: number;
};

type PatientLite = {
  id: string;
  name: string;
  phone?: string | null;
  hospitalId?: string;
  selfRegistrationPending?: boolean | null;
};

interface CreateAdditionalServicesBillProps {
  patient: PatientLite | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function newLineKey() {
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function CreateAdditionalServicesBill({
  patient,
  isOpen,
  onClose,
  onSuccess,
}: CreateAdditionalServicesBillProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [lines, setLines] = useState<LineDraft[]>([]);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [customDiscountAmount, setCustomDiscountAmount] = useState(0);
  const [discountType, setDiscountType] = useState<"percentage" | "custom">(
    "percentage"
  );
  const [splits, setSplits] = useState<PaymentSplitDraft[]>([
    { mode: PaymentMethod.CASH, amount: "" },
  ]);
  const [notes, setNotes] = useState("");
  const [paymentOption, setPaymentOption] = useState<"none" | "full" | "partial">(
    "none"
  );
  const [processingStep, setProcessingStep] = useState<
    "form" | "processing" | "complete"
  >("form");
  const [createdBillId, setCreatedBillId] = useState<string | null>(null);
  const [viewBillOpen, setViewBillOpen] = useState(false);

  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");
  const [newServicePrice, setNewServicePrice] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  /** Per-line filter text inside the service dropdown */
  const [lineServiceSearch, setLineServiceSearch] = useState<Record<string, string>>({});
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const [isDermatologyPatient, setIsDermatologyPatient] = useState(false);
  const [recommendedDoctorId, setRecommendedDoctorId] = useState("");

  const billingHospitalId = user?.hospitalId ?? patient?.hospitalId ?? undefined;

  const { data: doctors = [], isLoading: doctorsLoading } = useQuery({
    queryKey: ["hospital-doctors", billingHospitalId],
    queryFn: () => doctorApi.getDoctorsByHospital(billingHospitalId),
    enabled: isOpen && !!billingHospitalId,
    staleTime: 300_000,
  });

  const { data: patientRecord } = useQuery({
    queryKey: ["patient", patient?.id],
    queryFn: () => patientApi.getPatientById(patient!.id),
    enabled: isOpen && !!patient?.id,
  });
  const needsRegistrationSourceCapture = patientNeedsRegistrationSourceCapture(
    patientRecord ?? patient
  );

  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["additional-services"],
    queryFn: async () => {
      const res = await additionalServicesApi.list();
      return (res.data?.data ?? []) as AdditionalService[];
    },
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!isOpen) return;
    setLines([{ key: newLineKey(), serviceId: "", unitPrice: 0 }]);
    setDiscountPercentage(0);
    setCustomDiscountAmount(0);
    setDiscountType("percentage");
    setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
    setNotes("");
    setPaymentOption("none");
    setProcessingStep("form");
    setCreatedBillId(null);
    setViewBillOpen(false);
    setAddServiceOpen(false);
    setNewServiceName("");
    setNewServicePrice("");
    setServiceSearch("");
    setLineServiceSearch({});
    setCaptureDialogOpen(false);
    setIsDermatologyPatient(false);
    setRecommendedDoctorId("");
  }, [isOpen, patient?.id]);

  const serviceById = useMemo(() => {
    const map = new Map<string, AdditionalService>();
    for (const s of services) map.set(s.id, s);
    return map;
  }, [services]);

  const filteredServices = useMemo(() => {
    const q = serviceSearch.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => s.name.toLowerCase().includes(q));
  }, [services, serviceSearch]);

  const servicesForLine = (lineKey: string) => {
    const q = (lineServiceSearch[lineKey] || serviceSearch).trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => s.name.toLowerCase().includes(q));
  };

  const subtotal = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const price = Number(line.unitPrice) || 0;
        return sum + (price > 0 ? price : 0);
      }, 0),
    [lines]
  );

  const discountAmount =
    discountType === "percentage" && discountPercentage > 0
      ? (subtotal * discountPercentage) / 100
      : discountType === "custom" && customDiscountAmount > 0
        ? customDiscountAmount
        : 0;

  const taxableAmount = Math.max(0, subtotal - discountAmount);
  const gstAmount =
    isDermatologyPatient && taxableAmount > 0
      ? Math.round(taxableAmount * (DERMATOLOGY_GST_PERCENT / 100) * 100) / 100
      : 0;
  const totalAmount = Math.round((taxableAmount + gstAmount) * 100) / 100;
  const needsPayment = totalAmount > 0;
  const amountPaid = useMemo(
    () => (paymentOption === "none" ? 0 : getSplitsTotal(splits)),
    [paymentOption, splits]
  );
  const amountDue = totalAmount - amountPaid;

  const validLinesCount = lines.filter(
    (l) => l.serviceId && Number.isFinite(Number(l.unitPrice)) && Number(l.unitPrice) >= 0
  ).length;

  const createServiceMutation = useMutation({
    mutationFn: (data: { name: string; defaultPrice?: number }) =>
      additionalServicesApi.create(data),
    onSuccess: async (res) => {
      const created = res.data?.data as AdditionalService | undefined;
      await queryClient.invalidateQueries({ queryKey: ["additional-services"] });
      toast.success("Service added to dropdown");
      setAddServiceOpen(false);
      setNewServiceName("");
      setNewServicePrice("");
      if (created?.id) {
        setLines((prev) => {
          const emptyIdx = prev.findIndex((l) => !l.serviceId);
          if (emptyIdx >= 0) {
            const next = [...prev];
            next[emptyIdx] = {
              ...next[emptyIdx],
              serviceId: created.id,
              unitPrice: Number(created.defaultPrice) || 0,
            };
            return next;
          }
          return [
            ...prev,
            {
              key: newLineKey(),
              serviceId: created.id,
              unitPrice: Number(created.defaultPrice) || 0,
            },
          ];
        });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to add service");
    },
  });

  const createBillMutation = useMutation({
    mutationFn: (billData: BillCreateData) => billingApi.createBill(billData),
  });

  const processPaymentMutation = useMutation({
    mutationFn: async (payments: PaymentCreateData[]) =>
      paymentApi.bulkProcessPayments(payments),
  });

  const updateServicePrice = async (serviceId: string, unitPrice: number) => {
    try {
      await additionalServicesApi.update(serviceId, { defaultPrice: unitPrice });
    } catch {
      // Non-blocking: bill still succeeds if price memory fails
    }
  };

  const handleServiceSelect = (lineKey: string, serviceId: string) => {
    const svc = serviceById.get(serviceId);
    setLines((prev) =>
      prev.map((line) =>
        line.key === lineKey
          ? {
              ...line,
              serviceId,
              unitPrice: Number(svc?.defaultPrice) || 0,
            }
          : line
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patient?.id) {
      toast.error("Patient is required");
      return;
    }
    if (needsRegistrationSourceCapture) {
      setCaptureDialogOpen(true);
      return;
    }
    await processBillCreation();
  };

  const processBillCreation = async () => {
    if (!patient?.id) {
      toast.error("Patient is required");
      return;
    }
    if (!user?.hospitalId) {
      toast.error("Hospital context missing");
      return;
    }

    const validLines = lines.filter((l) => l.serviceId && Number(l.unitPrice) >= 0);
    if (validLines.length === 0) {
      toast.error("Add at least one service");
      return;
    }
    for (const line of validLines) {
      if (!Number.isFinite(Number(line.unitPrice)) || Number(line.unitPrice) < 0) {
        toast.error("Enter a valid amount for each service");
        return;
      }
    }

    if (discountType === "percentage" && (discountPercentage < 0 || discountPercentage > 100)) {
      toast.error("Discount percentage must be between 0 and 100");
      return;
    }
    if (discountType === "custom" && (customDiscountAmount < 0 || customDiscountAmount > subtotal)) {
      toast.error("Custom discount cannot exceed subtotal");
      return;
    }

    if (paymentOption !== "none" && needsPayment) {
      const splitErr = getSplitsError({ splits, maxTotal: totalAmount });
      if (splitErr) {
        toast.error(splitErr);
        return;
      }
    }

    setProcessingStep("processing");
    try {
      // Distribute bill-level discount across line items proportionally (same pattern as multi-item bills)
      const items = validLines.map((line) => {
        const unitPrice = Number(line.unitPrice) || 0;
        const lineShare =
          subtotal > 0 && discountAmount > 0
            ? (unitPrice / subtotal) * discountAmount
            : 0;
        const svc = serviceById.get(line.serviceId);
        return {
          itemType: BillType.PROCEDURE,
          description: svc?.name || "Additional Service",
          quantity: 1,
          unitPrice,
          totalPrice: unitPrice,
          discountAmount: Math.round(lineShare * 100) / 100,
          notes: notes || undefined,
        };
      });

      const billData: BillCreateData = {
        patientId: patient.id,
        hospitalId: user.hospitalId,
        paidAmount: 0,
        dueAmount: Number(totalAmount),
        status: BillStatus.DRAFT,
        billDate: new Date(),
        dueDate: new Date(),
        items,
        billNumberSeries: "ADDITIONAL_SERVICE",
        notes: notes
          ? `Additional Services: ${notes}`
          : "Additional Services",
        dermatologyGstApplicable: isDermatologyPatient,
        ...(recommendedDoctorId.trim()
          ? { recommendedDoctorId: recommendedDoctorId.trim() }
          : {}),
      };

      const billResponse = await createBillMutation.mutateAsync(billData);
      const billId = billResponse.data?.data?.id;
      if (!billId) throw new Error("Failed to create bill");

      // Remember last used price for each service
      await Promise.all(
        validLines.map((line) =>
          updateServicePrice(line.serviceId, Number(line.unitPrice) || 0)
        )
      );
      queryClient.invalidateQueries({ queryKey: ["additional-services"] });

      const amountPaidNow = paymentOption === "none" ? 0 : getSplitsTotal(splits);
      if (amountPaidNow > 0) {
        const payments: PaymentCreateData[] = splits.map((sp) => ({
          billId,
          amount: Number(sp.amount),
          paymentMethod: sp.mode,
          notes: notes || undefined,
        }));
        await processPaymentMutation.mutateAsync(payments);
      }

      setCreatedBillId(billId);
      setProcessingStep("complete");
      toast.success("Additional services bill created");
      onSuccess?.();
      setTimeout(() => {
        setViewBillOpen(true);
      }, 400);
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Failed to create additional services bill"
      );
      setProcessingStep("form");
    }
  };

  const isBusy =
    servicesLoading ||
    doctorsLoading ||
    createBillMutation.isPending ||
    processPaymentMutation.isPending ||
    processingStep === "processing";

  const handleClose = () => {
    if (isBusy && processingStep === "processing") return;
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen && !viewBillOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium flex items-center gap-2">
              <Syringe className="h-5 w-5 text-blue-700" />
              Additional Services
            </DialogTitle>
          </DialogHeader>

          {isBusy && processingStep !== "form" ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span className="text-sm text-gray-600">
                {processingStep === "processing" ? "Processing..." : "Loading..."}
              </span>
            </div>
          ) : processingStep === "complete" && !viewBillOpen ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <IndianRupee className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">Success!</h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                Bill created. Opening bill view…
              </p>
              <Button
                type="button"
                onClick={() => setViewBillOpen(true)}
                disabled={!createdBillId}
              >
                View / Print Bill
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {needsRegistrationSourceCapture ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  This patient self-registered online. Registration source must be recorded before billing.
                </p>
              ) : null}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="font-medium text-sm">{patient?.name}</div>
                    <div className="text-xs text-gray-500">
                      {patient?.phone || "Patient"}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="additional-services-doctor" className="text-sm font-medium">
                  Doctor <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <DoctorSearchableSelect
                  id="additional-services-doctor"
                  doctors={doctors}
                  value={recommendedDoctorId}
                  onValueChange={setRecommendedDoctorId}
                  placeholder="Select doctor for attribution…"
                  className="mt-1"
                  disabled={isBusy || !billingHospitalId}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  When set, revenue from this bill is attributed to the selected doctor in reports.
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="text-sm font-medium">Services</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAddServiceOpen(true)}
                      disabled={isBusy}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add service type
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setLines((prev) => [
                          ...prev,
                          { key: newLineKey(), serviceId: "", unitPrice: 0 },
                        ])
                      }
                      disabled={isBusy}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add line
                    </Button>
                  </div>
                </div>

                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    placeholder="Search services…"
                    className="pl-8"
                    disabled={isBusy || servicesLoading}
                  />
                </div>

                {serviceSearch.trim() && filteredServices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No services match “{serviceSearch.trim()}”. Use “Add service type” to create one.
                  </p>
                ) : null}

                {lines.map((line) => (
                  <div
                    key={line.key}
                    className="grid grid-cols-1 md:grid-cols-[1fr_140px_40px] gap-2 items-end"
                  >
                    <div>
                      <Label className="text-xs text-gray-500">Service</Label>
                      <Select
                        value={line.serviceId || undefined}
                        onValueChange={(v) => handleServiceSelect(line.key, v)}
                        onOpenChange={(open) => {
                          if (!open) {
                            setLineServiceSearch((prev) => {
                              const next = { ...prev };
                              delete next[line.key];
                              return next;
                            });
                          }
                        }}
                        disabled={isBusy || servicesLoading}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="px-2 pb-2 sticky top-0 bg-popover z-10">
                            <div className="relative">
                              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                              <Input
                                value={lineServiceSearch[line.key] ?? serviceSearch}
                                onChange={(e) =>
                                  setLineServiceSearch((prev) => ({
                                    ...prev,
                                    [line.key]: e.target.value,
                                  }))
                                }
                                placeholder="Type to search…"
                                className="h-8 pl-7 text-sm"
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          {servicesForLine(line.key).length === 0 ? (
                            <div className="px-2 py-3 text-sm text-muted-foreground">
                              No matching services
                            </div>
                          ) : (
                            servicesForLine(line.key).map((svc) => (
                              <SelectItem key={svc.id} value={svc.id}>
                                {svc.name}
                                {svc.defaultPrice > 0
                                  ? ` (₹${Number(svc.defaultPrice).toFixed(0)})`
                                  : ""}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Amount (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="mt-1"
                        value={Number.isFinite(line.unitPrice) ? line.unitPrice : 0}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setLines((prev) =>
                            prev.map((l) =>
                              l.key === line.key
                                ? { ...l, unitPrice: Number.isFinite(v) ? v : 0 }
                                : l
                            )
                          );
                        }}
                        disabled={isBusy}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-600"
                      disabled={isBusy || lines.length <= 1}
                      onClick={() =>
                        setLines((prev) => prev.filter((l) => l.key !== line.key))
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex items-start gap-3 rounded-lg border bg-slate-50/80 px-4 py-3">
                <Checkbox
                  id="dermatology-patient-as"
                  checked={isDermatologyPatient}
                  onCheckedChange={(v) => setIsDermatologyPatient(v === true)}
                  disabled={isBusy}
                />
                <div className="grid gap-1 leading-none">
                  <label
                    htmlFor="dermatology-patient-as"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Dermatology patient
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Adds {DERMATOLOGY_GST_PERCENT}% GST on services (after discount). Not shown on the
                    printed bill except as GST in the total.
                  </p>
                </div>
              </div>

              <div className="bg-white border rounded-xl shadow-sm p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Apply Discount
                  </h3>
                  <div className="flex gap-6 mb-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        checked={discountType === "percentage"}
                        onChange={() => setDiscountType("percentage")}
                        className="accent-blue-600"
                      />
                      Percentage (%)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        checked={discountType === "custom"}
                        onChange={() => setDiscountType("custom")}
                        className="accent-blue-600"
                      />
                      Custom Amount
                    </label>
                  </div>
                  {discountType === "percentage" ? (
                    <Input
                      type="number"
                      placeholder="e.g. 10"
                      min="0"
                      max="100"
                      value={discountPercentage}
                      onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                      disabled={isBusy}
                    />
                  ) : (
                    <Input
                      type="number"
                      placeholder={`Max: ₹${subtotal}`}
                      min="0"
                      max={subtotal}
                      value={customDiscountAmount}
                      onChange={(e) =>
                        setCustomDiscountAmount(Number(e.target.value))
                      }
                      disabled={isBusy}
                    />
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 mb-1">
                      <span>Discount</span>
                      <span>-₹{discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {gstAmount > 0 && (
                    <div className="flex justify-between text-sm mb-1">
                      <span>GST @ {DERMATOLOGY_GST_PERCENT}%</span>
                      <span>₹{gstAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold mt-2">
                    <span>Total</span>
                    <span>₹{totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Payment Details</Label>
                <div className="flex items-center gap-6 mb-2">
                  <div>
                    <Label className="text-xs text-gray-500">Total</Label>
                    <div className="text-lg font-semibold">
                      ₹{totalAmount.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Due</Label>
                    <div
                      className={`text-lg font-semibold ${
                        amountDue > 0 ? "text-red-600" : "text-green-600"
                      }`}
                    >
                      ₹{amountDue.toFixed(2)}
                    </div>
                  </div>
                </div>
                {!needsPayment ? (
                  <p className="text-sm text-muted-foreground rounded-md border bg-muted/40 px-3 py-2">
                    Bill total is ₹0. No payment is required.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={paymentOption === "full" ? "default" : "outline"}
                        className="flex-1"
                        onClick={() => {
                          setPaymentOption("full");
                          setSplits([
                            {
                              mode: PaymentMethod.CASH,
                              amount: String(totalAmount),
                            },
                          ]);
                        }}
                        disabled={isBusy}
                      >
                        Pay Full Amount
                      </Button>
                      <Button
                        type="button"
                        variant={
                          paymentOption === "partial" ? "default" : "outline"
                        }
                        className="flex-1"
                        onClick={() => {
                          setPaymentOption("partial");
                          setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
                        }}
                        disabled={isBusy}
                      >
                        Pay Partial Amount
                      </Button>
                    </div>
                    {paymentOption !== "none" && (
                      <PaymentSplitsEditor
                        splits={splits}
                        onChange={setSplits}
                        maxTotal={totalAmount}
                        disabled={isBusy}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Notes (Optional)</Label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                  className="w-full h-20 p-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={isBusy}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isBusy}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isBusy || validLinesCount === 0}>
                  {isBusy ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating…
                    </>
                  ) : (
                    "Generate Bill"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addServiceOpen} onOpenChange={setAddServiceOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add service type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Service name</Label>
              <Input
                className="mt-1"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="e.g. Nebulization"
              />
            </div>
            <div>
              <Label>Default amount (₹)</Label>
              <Input
                className="mt-1"
                type="number"
                min="0"
                step="0.01"
                value={newServicePrice}
                onChange={(e) => setNewServicePrice(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddServiceOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={
                  createServiceMutation.isPending || !newServiceName.trim()
                }
                onClick={() => {
                  const price =
                    newServicePrice === "" ? 0 : Number(newServicePrice);
                  if (!Number.isFinite(price) || price < 0) {
                    toast.error("Enter a valid default amount");
                    return;
                  }
                  createServiceMutation.mutate({
                    name: newServiceName.trim(),
                    defaultPrice: price,
                  });
                }}
              >
                {createServiceMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {patient?.id ? (
        <RegistrationSourceCaptureDialog
          open={captureDialogOpen}
          onOpenChange={setCaptureDialogOpen}
          patientId={patient.id}
          patientName={patient.name}
          onCaptured={() => {
            void queryClient.invalidateQueries({ queryKey: ["patient", patient.id] });
            void processBillCreation();
          }}
        />
      ) : null}

      <ViewBill
        billId={createdBillId}
        isOpen={viewBillOpen}
        onClose={() => {
          setViewBillOpen(false);
          onClose();
        }}
      />
    </>
  );
}
